import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import { Text } from '@spotify-internal/encore-web';

import styles from './ChatErrorMessage.module.css';

const FRIENDLY_MESSAGE = 'Something went wrong.';

// Best-effort friendly summary extraction. Handles the shapes we've seen:
// - gRPC:          "rpc error: ... details = \"<friendly>\" ..."
// - Anthropic API: "API Error: 400 {\"type\":\"error\",\"error\":{\"message\":\"<friendly>\",...}}"
// Falls back to the generic friendly message when nothing clean can be pulled.
function extractFriendlySummary(message: string): string {
  const grpc = message.match(/details\s*=\s*"([^"]+)"/);
  if (grpc) return grpc[1];

  const jsonStart = message.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed: unknown = JSON.parse(message.slice(jsonStart));
      if (
        parsed &&
        typeof parsed === 'object' &&
        'error' in parsed &&
        parsed.error &&
        typeof parsed.error === 'object' &&
        'message' in parsed.error &&
        typeof (parsed.error as { message: unknown }).message === 'string'
      ) {
        return (parsed.error as { message: string }).message;
      }
    } catch {
      // Fall through to the generic friendly message.
    }
  }

  return FRIENDLY_MESSAGE;
}

export const ChatErrorMessage = ({
  message,
  code,
}: {
  message: string;
  code?: string | null;
}) => {
  const [expanded, setExpanded] = useState(false);
  const summary = extractFriendlySummary(message);
  const hasDetails = message.length > 0 && summary !== message;

  return (
    <div className={styles.root}>
      <RiErrorWarningLine size={16} className={styles.icon} />
      <div className={styles.content}>
        <Text variant="bodySmall">
          {summary}
          {code ? (
            <Text as="span" variant="bodySmall" semanticColor="textSubdued">
              {` · ${code}`}
            </Text>
          ) : null}
        </Text>
        {hasDetails && (
          <>
            {expanded && (
              <div className={styles.details}>
                <Text variant="marginal" semanticColor="textSubdued">
                  {message}
                </Text>
              </div>
            )}
            <button
              type="button"
              className={styles.toggle}
              onClick={() => setExpanded(v => !v)}
             data-track-source-anchor="d1c4a10b1c;chat-error-message-button">
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
